import {FC, useContext, useEffect} from 'react';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';

import {TableToolbar} from './TableToolbar';
import {Paper, Table, TableContainer, TablePagination} from '@mui/material';
import {TableHeadContainer} from './TableHeadContainer';
import {TableBodyContainer} from './TableBody/TableBodyContainer';
import DeviceControl from './DeviceControl';

import {useDebouncePageable} from '../../../hooks/useDebouncePageable';

import {EventBusContext} from '../../../providers/EventBus';

import {devicesQuery} from '../../../store/deviceModels/selectors';
import {devicesPageable, devicesQueryTrigger, devicesState} from '../../../store/deviceModels/atoms';

import {IPageableTable} from '../../../shared/types/pageable.types';

export const DevicesList: FC = () => {
  const triggerQuery = useSetRecoilState(devicesQueryTrigger);
  const {totalElements} = useRecoilValue(devicesState);
  const [pageable, setPageable] = useRecoilState(devicesPageable);
  const {
    pageableToDebounce: {page, size},
    setPageableToDebounce,
  } = useDebouncePageable<IPageableTable>(pageable, setPageable);
  const {bus} = useContext(EventBusContext);

  const [{state, contents}, setDevices] = useRecoilStateLoadable(devicesQuery);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageableToDebounce({page: newPage, size});
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageableToDebounce({page: 0, size: parseInt(event.target.value, 10)});
    localStorage.setItem('devicesPerPage', event.target.value);
  };

  useEffect(() => {
    if (state === 'hasValue') setDevices(contents);

    if (state === 'hasError')
      bus?.emit('error', [
        contents,
        {
          toastId: 'devices',
        },
      ]);

    return () => {
      state !== 'loading' && triggerQuery((v) => v + 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      <Paper>
        <TableToolbar loadingState={state} />
        <TableContainer>
          <Table size="small">
            <TableHeadContainer />
            <TableBodyContainer rowsPerPage={size} />
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalElements}
            page={page}
            rowsPerPage={size}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>
      <DeviceControl />
    </>
  );
};
